import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { NSCommons } from '@ruby/configs/common.constants';
import { NSGuaranteesConstants } from '@ruby/configs/guarantees.constants';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import * as GuaranteesGridActions from '@ruby/modules/guarantees/store/actions/guarantees-grid.actions';
import * as fromGuaranteesGrid from '@ruby/modules/guarantees/store/reducers/guarantees-grid.reducer';
import * as GuaranteesGridSelectors from '@ruby/modules/guarantees/store/selectors/guarantees-grid.selectors';
import { EGuaranteesGridPaths, EGuaranteeType } from '@ruby/shared/enums/guarantees.enum';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import { ITransaction } from '@ruby/shared/models/commons/transaction.interface';
import { IGuaranteesRequest } from '@ruby/shared/models/guarantees/guarantees.interface';
import { IStorageLiftGuarantee } from '@ruby/shared/models/storage-guarantee/storage-guarantee.interface';

@Component({
  selector: 'ng-guarantees-grid-container',
  templateUrl: './guarantees-grid.container.html',
  styleUrls: ['./guarantees-grid.container.scss']
})
export class GuaranteesGridContainer implements OnDestroy {
  store$: Observable<fromGuaranteesGrid.State>;
  type!: EGuaranteesGridPaths;
  configs!: IGridConfigurations;
  requestObject!: Partial<IGuaranteesRequest>;
  acrSubscription!: Subscription;
  equipments?: Array<string>;
  redirectToDashboard = false;
  customer?: ICustomer;

  constructor(private store: Store<fromGuaranteesGrid.State>, private acr: ActivatedRoute, private router: Router) {
    this.parseTransaction();
    this.store$ = this.store.select(GuaranteesGridSelectors.selectTypeAndCustomer).pipe(
      filter((st: { type: EGuaranteesGridPaths; customer?: ICustomer }) => !!st.type),
      filter(() => !this.redirectToDashboard),
      tap((st: { type: EGuaranteesGridPaths; customer?: ICustomer }) => {
        if (this.customer && st.customer && this.customer !== st.customer && this.customer.type !== st.customer.type) {
          this.redirectToDashboard = true;
          this.router.navigate(['/home/dashboard']);
        } else {
          this.requestObject = {
            customerId: st.customer?.primarySixIds[0],
            guaranteeType: st.type === EGuaranteesGridPaths.LIFT ? EGuaranteeType.LIFT : EGuaranteeType.STORAGE,
            equipments: this.equipments,
            terminals: st.customer?.locations.map(l => l.locationName)
          };
          this.type = st.type;
          this.configs = NSGuaranteesConstants.gridPageConfigs[st.type];
          if (!!st.customer) {
            this.dispatchLoadData();
            this.configs = {
              ...this.configs,
              headerOptions: {
                ...NSGuaranteesConstants.config,
                headerDetails: NSCommons.gridHeader(st.customer)
              }
            };
          }
          this.customer = st.customer;
        }
      }),
      switchMap(() => this.store.select(GuaranteesGridSelectors.selectGuaranteesGridState))
    );
  }

  ngOnDestroy(): void {
    this.acrSubscription.unsubscribe();
    this.store.dispatch(GuaranteesGridActions.resetStorageGuaranteeActiveEquipments());
  }

  /**
   * Dispatch Load Data
   *
   * @summary Dispatch the correct data source
   * @returns void
   */
  dispatchLoadData(): void {
    if (this.requestObject.guaranteeType === EGuaranteeType.STORAGE) {
      this.store.dispatch(GuaranteesGridActions.loadStorageGuarantees({ data: this.requestObject }));
    } else {
      this.store.dispatch(GuaranteesGridActions.loadLiftGuarantees({ data: this.requestObject }));
    }
  }

  private parseTransaction(): void {
    this.acrSubscription = this.acr.data.pipe(
      map((data: Data) => data.transaction)
    ).subscribe((query: ITransaction) => {
      if (query && query.parameters.storageLiftGuarantee) {
        const storageLiftGuarantee: IStorageLiftGuarantee = JSON.parse(query.parameters.storageLiftGuarantee) as IStorageLiftGuarantee;
        this.equipments = storageLiftGuarantee.equipments;
      }
    });
  }
}
